import { DB } from "./deviceStorage";
import { getLiveCollectionCache, setLiveCollectionCache } from "../sync/cacheApi";
import { pushCollection } from "../sync/collectionApi";
import { isDeletedItem, stripDeletedItems, parseSyncTs, stableJson, itemSyncTs } from "./syncUtils";
import { createClientId, today } from "./formatters";
import { SYSTEM_LISTS_DEFAULTS, COST_CENTERS } from "./globals";
import { getAccounts, getUserDeletionLog, pruneDeletedAccounts, pruneDeletedEmployees } from "./authUtils";
import { choosePreferredOperationalRecord, isOperationalCollection } from "./operationalRecordUtils";

function pickPreferredFarmRecord(localFarm, serverFarm) {
  if (!localFarm) return serverFarm;
  if (!serverFarm) return localFarm;

  const localDeleted = !!(localFarm?._deleted || localFarm?.deletedAt || localFarm?.deleted_at);
  const serverDeleted = !!(serverFarm?._deleted || serverFarm?.deletedAt || serverFarm?.deleted_at);
  const localTs = parseSyncTs(localFarm?.deletedAt || localFarm?.deleted_at || localFarm?.updatedAt || localFarm?.createdAt || "1970");
  const serverTs = parseSyncTs(serverFarm?.deletedAt || serverFarm?.deleted_at || serverFarm?.updatedAt || serverFarm?.createdAt || "1970");

  if (localDeleted !== serverDeleted) {
    if (localTs === serverTs) return localDeleted ? localFarm : serverFarm;
    return localTs > serverTs ? localFarm : serverFarm;
  }

  const localName = String(localFarm?.name || "").trim();
  const serverName = String(serverFarm?.name || "").trim();
  if (!localDeleted && !serverDeleted && String(localFarm?.id || serverFarm?.id || "") === "farm1" && localName && serverName && localName !== serverName) {
    if (localName !== "Fazenda Principal") return { ...serverFarm, ...localFarm };
    if (serverName !== "Fazenda Principal") return { ...localFarm, ...serverFarm };
  }

  return localTs >= serverTs ? localFarm : serverFarm;
}

function buildCollectionIdentityKeys(item, collectionName = "") {
  const col = String(collectionName || "").trim();
  const norm = (v) => String(v || "").trim().toLowerCase();
  const digits = (v) => String(v || "").replace(/\D+/g, "");
  const farmId = String(item?.farmId || item?.primaryFarmId || "").trim();
  const keys = [];
  const pushKey = (value) => {
    const key = String(value || "").trim();
    if (!key || keys.includes(key)) return;
    keys.push(key);
  };

  const clientId = String(item?.clientId || "").trim();
  if (["services","occurrences","orders","inspections","financial","vet","poi"].includes(col) && clientId) pushKey(`${col}:client:${clientId}`);
  const id = String(item?.id || "").trim();
  if (id) pushKey(`id:${id}`);

  if (col === "employees") {
    const linkedAccountId = String(item?.linkedAccountId || "").trim();
    const userId = String(item?.userId || "").trim();
    const username = norm(item?.username);
    const cpf = digits(item?.cpf);
    const email = norm(item?.email);
    if (linkedAccountId) pushKey(`emp:acc:${linkedAccountId}`);
    if (userId) pushKey(`emp:user:${userId}`);
    if (username) pushKey(`emp:usr:${farmId}:${username}`);
    if (cpf) pushKey(`emp:cpf:${cpf}`);
    if (email) pushKey(`emp:mail:${email}`);
  }

  if (col === "poi") {
    const name = norm(item?.name || item?.title || item?.label || item?.description);
    const category = norm(item?.category);
    const costCenter = norm(item?.costCenter);
    const linkedStallId = String(item?.linkedStallId || "").trim();
    const pastureIds = (Array.isArray(item?.pastureIds) ? item.pastureIds : [])
      .map(v => String(v || "").trim())
      .filter(Boolean)
      .sort()
      .join("|");
    const lat = String(item?.lat ?? item?.latitude ?? item?.pin?.lat ?? "").trim();
    const lng = String(item?.lng ?? item?.longitude ?? item?.pin?.lng ?? "").trim();
    const coordKey = lat || lng ? `${lat},${lng}` : "";
    if (name) pushKey(`poi:biz:${farmId}:${name}:${category}:${costCenter}:${linkedStallId}:${pastureIds}:${coordKey}`);
    if (name) pushKey(`poi:name:${farmId}:${name}:${category}:${costCenter}`);
  }

  if (col === "stock") {
    const barcode = norm(item?.barcode);
    const sku = norm(item?.sku || item?.code);
    const name = norm(item?.name || item?.title || item?.label);
    const brand = norm(item?.brand);
    const supplier = norm(item?.supplier || item?.supplierName);
    const unit = norm(item?.unit);
    const category = norm(item?.category);
    const pkgRef = norm(item?.pkgUnitRef || item?.refUnit);
    if (barcode) pushKey(`stock:barcode:${barcode}`);
    if (sku) pushKey(`stock:sku:${sku}`);
    if (name) pushKey(`stock:biz:${name}:${brand}:${supplier}:${unit}:${category}:${pkgRef}`);
    if (name) pushKey(`stock:name:${name}:${brand}:${unit}`);
  }

  if (col === "services" || col === "occurrences" || col === "orders" || col === "inspections" || col === "financial" || col === "vet") {
    const stableClientId = String(item?.clientId || "").trim();
    if (stableClientId) pushKey(`${col}:client:${stableClientId}`);
  }

  const title = norm(item?.title || item?.name || item?.label || item?.description);
  const recordNumber = norm(item?.recordNumber || item?.number || item?.code || '');
  const transactionalCollections = new Set(['services','occurrences','orders','inspections','financial','vet','notifications','stockMovements','fueling','machineryMaintenance']);
  if (recordNumber) pushKey(`${col || 'generic'}:record:${farmId}:${recordNumber}`);
  if (title && (!transactionalCollections.has(col) || keys.length === 0)) pushKey(`${col || 'generic'}:farm:${farmId}:${title}`);
  if (!keys.length) pushKey(`${col || 'generic'}:fallback:${stableJson(item || {})}`);
  return keys;
}

function buildCollectionIdentityKey(item, collectionName = "") {
  return buildCollectionIdentityKeys(item, collectionName)[0] || `${String(collectionName || 'generic').trim() || 'generic'}:fallback:${stableJson(item || {})}`;
}

function collectionItemsReferSameIdentity(a, b, collectionName = "") {
  const aKeys = buildCollectionIdentityKeys(a, collectionName);
  const bKeys = buildCollectionIdentityKeys(b, collectionName);
  return aKeys.some(key => bKeys.includes(key));
}


function normalizeOrderStatusText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function orderStatusStrength(value = "") {
  const status = normalizeOrderStatusText(value);
  if (!status) return 0;
  if (status.includes("neg")) return 6;
  if (status.includes("cancel")) return 6;
  if (status.includes("conclu") || status.includes("receb")) return 5;
  if (status.includes("transito") || status.includes("envi")) return 4;
  if (status.includes("aprov") || status.includes("parcial")) return 3;
  if (status.includes("analis") || status.includes("cot")) return 2;
  if (status.includes("pend") || status.includes("criad")) return 1;
  return 0;
}

function orderItemsStatusStrength(items = []) {
  return (Array.isArray(items) ? items : []).reduce((max, item) => Math.max(max, orderStatusStrength(item?.itemStatus || item?.status)), 0);
}

function orderWorkflowStrength(item = {}) {
  if (!item || typeof item !== "object") return 0;
  return Math.max(orderStatusStrength(item?.status), orderItemsStatusStrength(item?.items));
}

function applyOrderWorkflowFloor(merged = {}, left = {}, right = {}) {
  const leftStrength = orderWorkflowStrength(left);
  const rightStrength = orderWorkflowStrength(right);
  const mergedStrength = orderWorkflowStrength(merged);
  const winner = leftStrength >= rightStrength ? left : right;
  const winnerStrength = Math.max(leftStrength, rightStrength);
  if (winnerStrength > 0 && winnerStrength > mergedStrength) {
    return {
      ...merged,
      ...winner,
      serverRevision: Math.max(Number(merged?.serverRevision || 0), Number(left?.serverRevision || 0), Number(right?.serverRevision || 0)),
    };
  }
  return {
    ...merged,
    serverRevision: Math.max(Number(merged?.serverRevision || 0), Number(left?.serverRevision || 0), Number(right?.serverRevision || 0)),
  };
}

function normalizeNotificationReadEntries(entries = []) {
  const byRecipient = new Map();
  const list = Array.isArray(entries) ? entries : [];
  const buildKey = (entry) => {
    const accountId = String(entry?.accountId || '').trim();
    const userId = String(entry?.userId || '').trim();
    const username = String(entry?.username || '').trim().toLowerCase();
    const name = String(entry?.name || '').trim().toLowerCase();
    return accountId ? `acc:${accountId}` : userId ? `usr:${userId}` : username ? `name:${username}` : name ? `display:${name}` : '';
  };
  list.filter(Boolean).forEach((entry) => {
    const key = buildKey(entry);
    if (!key) return;
    const current = byRecipient.get(key) || null;
    const currentTs = parseSyncTs(current?.readAt || current?.updatedAt || current?.createdAt || '1970-01-01T00:00:00.000Z');
    const nextTs = parseSyncTs(entry?.readAt || entry?.updatedAt || entry?.createdAt || '1970-01-01T00:00:00.000Z');
    if (!current || nextTs >= currentTs) {
      byRecipient.set(key, {
        ...current,
        ...entry,
        accountId: String(entry?.accountId || current?.accountId || '').trim(),
        userId: String(entry?.userId || current?.userId || '').trim(),
        username: String(entry?.username || current?.username || '').trim(),
        name: String(entry?.name || current?.name || '').trim(),
        readAt: String(entry?.readAt || current?.readAt || '').trim(),
      });
    }
  });
  return Array.from(byRecipient.values()).sort((a, b) => parseSyncTs(a?.readAt || 0) - parseSyncTs(b?.readAt || 0));
}

function mergeNotificationCollectionItem(localItem, serverItem) {
  const localReadBy = normalizeNotificationReadEntries(localItem?.readBy);
  const serverReadBy = normalizeNotificationReadEntries(serverItem?.readBy);
  const mergedReadBy = normalizeNotificationReadEntries([...serverReadBy, ...localReadBy]);
  const localBaseTs = itemSyncTs(localItem);
  const serverBaseTs = itemSyncTs(serverItem);
  const localReadStateTs = parseSyncTs(localItem?.readStateUpdatedAt || '');
  const serverReadStateTs = parseSyncTs(serverItem?.readStateUpdatedAt || '');
  const latestReadEntryTs = mergedReadBy.reduce((maxTs, entry) => Math.max(maxTs, parseSyncTs(entry?.readAt || '')), 0);
  const preferred = localBaseTs >= serverBaseTs ? localItem : serverItem;
  const secondary = preferred === localItem ? serverItem : localItem;
  const mergedUpdatedTs = Math.max(localBaseTs, serverBaseTs, localReadStateTs, serverReadStateTs, latestReadEntryTs);
  const mergedUpdatedAt = mergedUpdatedTs > 0 ? new Date(mergedUpdatedTs).toISOString() : String(preferred?.updatedAt || secondary?.updatedAt || '').trim();
  const mergedReadStateUpdatedAtTs = Math.max(localReadStateTs, serverReadStateTs, latestReadEntryTs);
  const mergedReadStateUpdatedAt = mergedReadStateUpdatedAtTs > 0 ? new Date(mergedReadStateUpdatedAtTs).toISOString() : String(preferred?.readStateUpdatedAt || secondary?.readStateUpdatedAt || '').trim();
  return {
    ...secondary,
    ...preferred,
    openedInApp: !!(localItem?.openedInApp || serverItem?.openedInApp),
    readBy: mergedReadBy,
    readStateUpdatedAt: mergedReadStateUpdatedAt,
    updatedAt: mergedUpdatedAt,
    updatedBy: String(preferred?.updatedBy || secondary?.updatedBy || '').trim(),
  };
}

function choosePreferredCollectionItem(localItem, serverItem, collectionName = "") {
  if (!localItem) return serverItem;
  if (!serverItem) return localItem;
  if (collectionName === "employees") {
    const score = (item) => {
      let total = 0;
      if (item?.linkedAccountId) total += 10;
      if (item?.userId) total += 8;
      if (item?.username) total += 6;
      if (item?.cpf) total += 5;
      if (item?.email) total += 4;
      if (item?.phone) total += 1;
      if (item?.updatedAt) total += 1;
      return total;
    };
    const localScore = score(localItem);
    const serverScore = score(serverItem);
    if (localScore !== serverScore) return localScore >= serverScore ? { ...serverItem, ...localItem } : { ...localItem, ...serverItem };
  }
  if (collectionName === "notifications") return mergeNotificationCollectionItem(localItem, serverItem);
  if (isOperationalCollection(collectionName)) {
    // v2.221.33: UDT local em registro já confirmado é uma operação pendente.
    // Um snapshot/pull do servidor sem esse UDT não é ACK/rejeição; manter a
    // alteração local até o servidor confirmar explicitamente a operação.
    const localHasUDT = !!(!localItem?.pendingLocalCreate
      && !String(localItem?.recordNumber || '').toUpperCase().endsWith('-PRV')
      && (localItem?.pendingLocalUpdate || localItem?.updatePending || (localItem?.pendingLocalChange && localItem?.pendingOperationId)));
    const serverStillPending = !!(serverItem?.pendingLocalUpdate || serverItem?.updatePending || serverItem?.pendingLocalChange);
    const localOp = String(localItem?.pendingOperationId || localItem?.localOperationId || '').trim();
    const serverOp = String(serverItem?.serverAckOperationId || serverItem?.ackedOperationId || serverItem?.operationId || serverItem?.pendingOperationId || '').trim();
    const serverHasExplicitAck = !!(serverItem?.serverAckAt || serverItem?.serverAckOperationId || serverItem?.ackedOperationId);
    const serverAckedSameOp = !!(localHasUDT
      && !serverStillPending
      && serverHasExplicitAck
      && (!localOp || !serverOp || localOp === serverOp));
    if (localHasUDT && !serverAckedSameOp) {
      return {
        ...serverItem,
        ...localItem,
        serverRevision: Math.max(Number(localItem?.serverRevision || 0), Number(serverItem?.serverRevision || 0)),
        pendingLocalUpdate: true,
        pendingLocalChange: true,
        syncStatus: 'pending',
      };
    }

    // v2.221.20: servidor tem autoridade explícita quando confirmou a criação PRV.
    // Sem esta guarda, choosePreferredOperationalRecord usa updatedAt local
    // (sempre mais recente por markOperationalCreatePending) e reverte o PRV
    // indefinidamente — esse era o caminho pós-push não coberto pela v2.221.19.
    // Múltiplos sinais de PRV = redundância defensiva.
    const localHasPRV = !!(localItem?.pendingLocalCreate
      || String(localItem?.recordNumber || '').toUpperCase().endsWith('-PRV')
      || (localItem?.syncStatus === 'pending' && !localItem?.serverConfirmed));
    const serverConfirmedCreate = !!(serverItem?.serverConfirmed
      && serverItem?.pendingLocalCreate === false
      && serverItem?.syncStatus === 'synced');
    if (localHasPRV && serverConfirmedCreate) {
      return {
        ...localItem,
        ...serverItem,
        pendingLocalCreate: false,
        pendingLocalChange: false,
        syncStatus: 'synced',
      };
    }
    const preferredOperational = choosePreferredOperationalRecord(localItem, serverItem);
    if (collectionName === "orders") {
      return applyOrderWorkflowFloor(preferredOperational, localItem, serverItem);
    }
    return preferredOperational;
  }
  const localTs = itemSyncTs(localItem);
  const serverTs = itemSyncTs(serverItem);
  if (localTs !== serverTs) return localTs >= serverTs ? localItem : serverItem;
  const localDeleted = isDeletedItem(localItem);
  const serverDeleted = isDeletedItem(serverItem);
  if (localDeleted !== serverDeleted) return localDeleted ? localItem : serverItem;
  return stableJson(localItem) >= stableJson(serverItem) ? localItem : serverItem;
}

function dedupeCollectionForBootstrap(list, collectionName = "") {
  const arr = Array.isArray(list) ? list : [];
  const out = [];
  for (const rawItem of arr) {
    const item = rawItem && typeof rawItem === "object" ? { ...rawItem } : rawItem;
    const idx = out.findIndex(existing => collectionItemsReferSameIdentity(existing, item, collectionName));
    if (idx < 0) {
      out.push(item);
      continue;
    }
    out[idx] = choosePreferredCollectionItem(out[idx], item, collectionName);
  }
  return out;
}

function mergeBootstrapVisibleWithServer(localVisible, serverRaw, collectionName = "") {
  const locRawArr = Array.isArray(localVisible) ? localVisible : [];
  const srvRawArr = Array.isArray(serverRaw) ? serverRaw : [];
  const locArr = dedupeCollectionForBootstrap(locRawArr, collectionName);
  const srvArr = dedupeCollectionForBootstrap(srvRawArr, collectionName);
  if (locArr.length === 0 && srvArr.length === 0) return { raw: [], visible: [], needsPush: false };

  const merged = [];
  let needsPush = false;
  const consumedServer = new Set();
  const consumedLocal = new Set();

  for (let li = 0; li < locArr.length; li += 1) {
    const locItem = locArr[li];
    const si = srvArr.findIndex((srvItem, idx) => !consumedServer.has(idx) && collectionItemsReferSameIdentity(locItem, srvItem, collectionName));
    if (si < 0) {
      merged.push(locItem);
      needsPush = true;
      consumedLocal.add(li);
      continue;
    }
    const srvItem = srvArr[si];
    consumedLocal.add(li);
    consumedServer.add(si);
    const chosen = choosePreferredCollectionItem(locItem, srvItem, collectionName);
    merged.push(chosen);
    if (stableJson(chosen) !== stableJson(srvItem)) needsPush = true;
  }

  for (let si = 0; si < srvArr.length; si += 1) {
    if (consumedServer.has(si)) continue;
    merged.push(srvArr[si]);
  }

  const raw = dedupeCollectionForBootstrap(merged, collectionName);
  return { raw, visible: stripDeletedItems(raw), needsPush };
}

// v3.9.12: digest canônico estável para employees — ignora campos voláteis.
// Compara apenas campos de identidade/negócio que representam mudança real.
function _canonicalEmployeeForDigest(emp) {
  if (!emp || typeof emp !== "object") return {};
  return {
    id:              String(emp.id              || ""),
    name:            String(emp.name            || ""),
    firstName:       String(emp.firstName       || ""),
    lastName:        String(emp.lastName        || ""),
    role:            String(emp.role            || ""),
    status:          String(emp.status          || ""),
    costCenter:      String(emp.costCenter      || ""),
    phone:           String(emp.phone           || ""),
    cpf:             String(emp.cpf             || ""),
    email:           String(emp.email           || ""),
    linkedAccountId: String(emp.linkedAccountId || ""),
    accountId:       String(emp.accountId       || ""),
    userName:        String(emp.userName || emp.username || ""),
  };
}
function _employeesCanonicalDigest(list) {
  const arr = Array.isArray(list) ? list : [];
  return JSON.stringify(
    arr
      .filter(Boolean)
      .map(_canonicalEmployeeForDigest)
      .sort((a, b) =>
        (a.linkedAccountId || a.id || a.email)
          .localeCompare(b.linkedAccountId || b.id || b.email)
      )
  );
}
// v3.9.12: gate de in-flight e digest por farmId para evitar push loop de employees.
const _employeesPushInFlight = {};
const _employeesPushLastDigest = {};

async function syncFarmCollaboratorAccountsToEmployees(farmId, actorName="sistema", sourceEmployees=null) {
  if (!farmId) return [];
  // v3.9.11: não executar em paralelo para a mesma farm.
  if (_employeesPushInFlight[farmId]) return [];
  try {
    const normalize = (v) => String(v || "").trim().toLowerCase();
    const normalizeDigits = (v) => String(v || "").replace(/\D+/g, "");
    const buildEmployeeIdentityKeys = (emp) => {
      const keys = [];
      const linkedAccountId = String(emp?.linkedAccountId || "").trim();
      const userId = String(emp?.userId || "").trim();
      const username = normalize(emp?.username);
      const cpf = normalizeDigits(emp?.cpf);
      const email = normalize(emp?.email);
      if (linkedAccountId) keys.push(`acc:${linkedAccountId}`);
      if (userId) keys.push(`user:${userId}`);
      if (username) keys.push(`usr:${username}`);
      if (cpf) keys.push(`cpf:${cpf}`);
      if (email) keys.push(`mail:${email}`);
      return keys;
    };
    const buildAccountIdentityKeys = (acc) => {
      const keys = [];
      const accId = String(acc?.id || "").trim();
      const userId = String(acc?.userId || "").trim();
      const username = normalize(acc?.username);
      const cpf = normalizeDigits(acc?.cpf);
      const email = normalize(acc?.email);
      if (accId) keys.push(`acc:${accId}`);
      if (userId) keys.push(`user:${userId}`);
      if (username) keys.push(`usr:${username}`);
      if (cpf) keys.push(`cpf:${cpf}`);
      if (email) keys.push(`mail:${email}`);
      return keys;
    };
    const dedupeEmployees = (list) => {
      const keyToIndex = new Map();
      const out = [];
      const chooseBetter = (a, b) => {
        const score = (emp) => {
          let s = 0;
          if (emp?.linkedAccountId) s += 10;
          if (emp?.username) s += 6;
          if (emp?.cpf) s += 4;
          if (emp?.email) s += 2;
          if (emp?.phone) s += 1;
          if (Array.isArray(emp?.sharedFarms) && emp.sharedFarms.length) s += 1;
          if (emp?.updatedAt) s += 1;
          return s;
        };
        const aScore = score(a);
        const bScore = score(b);
        if (bScore > aScore) return { ...a, ...b, id: String(a?.id || b?.id || createClientId("emp")) };
        return { ...b, ...a, id: String(a?.id || b?.id || createClientId("emp")) };
      };

      for (const item of Array.isArray(list) ? list : []) {
        const emp = { ...(item || {}) };
        const keys = buildEmployeeIdentityKeys(emp);
        const foundIndices = [...new Set(keys.map(k => keyToIndex.get(k)).filter(i => i !== undefined))];
        if (!foundIndices.length) {
          const idx = out.push(emp) - 1;
          for (const k of keys) keyToIndex.set(k, idx);
          continue;
        }
        const targetIdx = foundIndices[0];
        let merged = chooseBetter(out[targetIdx], emp);
        for (let i = 1; i < foundIndices.length; i++) {
          const dupIdx = foundIndices[i];
          if (dupIdx === targetIdx || !out[dupIdx]) continue;
          merged = chooseBetter(merged, out[dupIdx]);
          out[dupIdx] = null;
        }
        out[targetIdx] = merged;
        for (const k of buildEmployeeIdentityKeys(merged)) keyToIndex.set(k, targetIdx);
      }

      return out.filter(Boolean);
    };

    const isOperationalPendingEmployee = (emp) => !!(emp && typeof emp === "object" && (emp.pendingLocalCreate === true || emp.pendingLocalUpdate === true || emp.pendingLocalChange === true));
    const employeeRecordKeys = (emp) => {
      const keys = [];
      const push = (value) => {
        const key = String(value || "").trim();
        if (key && !keys.includes(key)) keys.push(key);
      };
      push(emp?.clientId ? `client:${emp.clientId}` : "");
      push(emp?.id ? `id:${emp.id}` : "");
      for (const key of buildEmployeeIdentityKeys(emp)) push(key);
      return keys;
    };
    const preservePendingOperationalEmployees = (mergedList, localList) => {
      const pendingRecords = (Array.isArray(localList) ? localList : []).filter(isOperationalPendingEmployee);
      if (!pendingRecords.length) return Array.isArray(mergedList) ? mergedList : [];
      let out = (Array.isArray(mergedList) ? mergedList : []).map(emp => ({ ...(emp || {}) }));
      const rebuildIndex = () => {
        const index = new Map();
        out.forEach((emp, idx) => {
          for (const key of employeeRecordKeys(emp)) index.set(key, idx);
        });
        return index;
      };
      let index = rebuildIndex();
      for (const pending of pendingRecords) {
        const keys = employeeRecordKeys(pending);
        let foundIndex;
        for (const key of keys) {
          const idx = index.get(key);
          if (idx !== undefined) {
            foundIndex = idx;
            break;
          }
        }
        const found = foundIndex;
        if (found === undefined) {
          out.push({ ...pending });
          index = rebuildIndex();
          continue;
        }
        const currentItem = out[found];
        const currentTs = itemSyncTs(currentItem);
        const pendingTs = itemSyncTs(pending);
        const currentPending = isOperationalPendingEmployee(currentItem);
        if (!currentPending || pendingTs >= currentTs) {
          out[found] = { ...currentItem, ...pending };
          index = rebuildIndex();
        }
      }
      return out;
    };


    const accounts = await getAccounts();
    const deletionLog = await getUserDeletionLog(actorName);
    const accountCollabs = pruneDeletedAccounts(accounts || [], deletionLog).filter(acc =>
      acc?.role === "solicitante" &&
      acc?.active !== false &&
      Array.isArray(acc?.farmIds) &&
      acc.farmIds.includes(farmId)
    );

    const empKey = `fz:${farmId}:employees`;
    const liveCurrent = getLiveCollectionCache(empKey);
    const currentBase = Array.isArray(sourceEmployees)
      ? sourceEmployees
      : (Array.isArray(liveCurrent) ? liveCurrent : ((await DB.get(empKey)) || []));
    const current = dedupeEmployees(pruneDeletedEmployees(Array.isArray(currentBase) ? currentBase : [], deletionLog));
    // v3.9.12: usar digest canônico desde o início — mesmo critério do bloco final.
    const _currentDigest = _employeesCanonicalDigest(current);
    let changed = _currentDigest !== _employeesCanonicalDigest(Array.isArray(currentBase) ? currentBase : []);

    // Preserve every existing employee record and only enrich/update the ones
    // that are actually linked to active solicitante accounts for this farm.
    // Also dedupe by canonical identity so the same collaborator is not materialized twice
    // after local save + account enrichment + bootstrap sync.
    const next = current.map(emp => ({ ...emp }));
    const identityIndex = new Map();
    const registerIdentity = (emp, idx) => {
      for (const key of buildEmployeeIdentityKeys(emp)) {
        if (key) identityIndex.set(key, idx);
      }
    };
    next.forEach((emp, idx) => registerIdentity(emp, idx));

    for (const acc of accountCollabs) {
      const now = new Date().toISOString();
      const keys = buildAccountIdentityKeys(acc);
      const foundIndices = [...new Set(keys.map(k => identityIndex.get(k)).filter(i => i !== undefined))];
      const idx = foundIndices.length ? foundIndices[0] : -1;
      const existing = idx >= 0 ? next[idx] : null;
      const merged = {
        ...(existing || {}),
        id: String(existing?.id || createClientId("emp")),
        farmId: existing?.farmId || farmId,
        linkedAccountId: acc.id,
        userId: existing?.userId || acc.userId || "",
        name: acc.name || acc.username || existing?.name || "Colaborador",
        username: acc.username || existing?.username || "",
        email: acc.email || existing?.email || "",
        phone: acc.phone || existing?.phone || "",
        status: acc.active === false ? "Inativo" : "Ativo",
        role: existing?.role || (SYSTEM_LISTS_DEFAULTS.FUNC_ROLES?.[0] || "Colaborador(a)"),
        costCenter: existing?.costCenter || (COST_CENTERS?.[0] || "Sede"),
        cpf: existing?.cpf || acc.cpf || "",
        rg: existing?.rg || "",
        birthDate: existing?.birthDate || "",
        pix: existing?.pix || "",
        salary: existing?.salary || 0,
        hired: existing?.hired || today(),
        notes: existing?.notes || "",
        warnings: existing?.warnings || [],
        gestorVisibleFields: existing?.gestorVisibleFields || ["hired"],
        collabLevel: acc.collabLevel || existing?.collabLevel || 1,
        primaryFarmId: existing?.primaryFarmId || farmId,
        sharedFarms: existing?.sharedFarms || [],
        createdAt: existing?.createdAt || now,
        updatedAt: now,
        updatedBy: actorName,
      };

      if (idx >= 0) {
        if (JSON.stringify(existing) !== JSON.stringify(merged)) {
          next[idx] = merged;
          changed = true;
        }
        registerIdentity(merged, idx);

        if (foundIndices.length > 1) {
          for (let i = foundIndices.length - 1; i >= 1; i--) {
            const dupIdx = foundIndices[i];
            if (dupIdx === idx || !next[dupIdx]) continue;
            next.splice(dupIdx, 1);
            changed = true;
          }
          identityIndex.clear();
          next.forEach((emp, rebuiltIdx) => registerIdentity(emp, rebuiltIdx));
        }
      } else {
        next.push(merged);
        changed = true;
        registerIdentity(merged, next.length - 1);
      }
    }

    let finalEmployees = pruneDeletedEmployees(dedupeEmployees(next), deletionLog).map(emp => ({
      ...emp,
      id: String(emp?.id || createClientId("emp")),
      farmId: emp?.farmId || farmId,
      primaryFarmId: emp?.primaryFarmId || emp?.farmId || farmId,
    }));
    // v2.240.00: o sync de contas → colaboradores é um caminho paralelo ao sync operacional.
    // Preserve PRV/UDT locais para que um snapshot de accounts não sobrescreva colaboradores
    // ainda não confirmados pelo ACK de employees.
    finalEmployees = preservePendingOperationalEmployees(finalEmployees, current);
    // v3.9.12: usar digest canônico — ignora campos voláteis (pendingLocal*, sync flags, etc).
    const _finalDigest = _employeesCanonicalDigest(finalEmployees);
    changed = _currentDigest !== _finalDigest;

    if (changed) {
      // v3.9.12: verificar digest ANTES de DB.set/dispatch/push.
      // Se o digest canônico já foi enviado com sucesso, não fazer nada —
      // evita que DB.set/dispatchEvent reiniciem o ciclo sem mudança real.
      if (_employeesPushInFlight[farmId]) return current;
      if (_employeesPushLastDigest[farmId] === _finalDigest) return finalEmployees;

      // Há mudança real canônica — gravar, notificar e enviar.
      try {
        _employeesPushInFlight[farmId] = true;
        await DB.set(empKey, finalEmployees);
        setLiveCollectionCache(empKey, finalEmployees);
        window.dispatchEvent(new CustomEvent("fz:sync:updated", {
          detail: { key: empKey, source: "employees-account-merge" },
        }));
        const currentVersion = (await DB.get(`fz:${farmId}:employees:_ver`)) || 0;
        const res = await pushCollection(farmId, "employees", finalEmployees, currentVersion, actorName);
        if (res?.version) {
          await DB.set(`fz:${farmId}:employees:_ver`, res.version);
          // Salvar digest apenas após confirmação — push falho não bloqueia próxima tentativa.
          _employeesPushLastDigest[farmId] = _finalDigest;
        }
      } catch (_pushErr) {
      } finally {
        _employeesPushInFlight[farmId] = false;
      }
      return finalEmployees;
    }
    return current;
  } catch (e) {
    if (Array.isArray(sourceEmployees)) return sourceEmployees;
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SYSTEM LISTS — customizable dropdown lists (global, not per-farm)
// ═══════════════════════════════════════════════════════════════════════════════


export {
  pickPreferredFarmRecord,
  buildCollectionIdentityKeys, buildCollectionIdentityKey,
  collectionItemsReferSameIdentity, choosePreferredCollectionItem,
  dedupeCollectionForBootstrap, mergeBootstrapVisibleWithServer,
  syncFarmCollaboratorAccountsToEmployees,
};
